import { useEffect, useRef } from 'react';

import { differenceInHours } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FeatureFlags, useFeatureFlag } from '~/src/app/utils/featureflag';
import { useBillingAccount } from '~/src/hooks/useBillingAccount';
import { useSnackbar } from '~/src/hooks/useSnackbar';
import { billingAccountService } from '~/src/services/BillingAccountService';
import { useFetchBilling } from '~/src/views/Account/pages/Billing/hooks/useFetchBilling';

const getExpirationDate = (month: null | number, year: null | number) => {
  if (!month || !year) return 'unknown';

  const expirationDate = new Date(year, month - 1);
  return Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(expirationDate);
};

export const useGlobalAlerts = () => {
  const alertFlag = useFeatureFlag(FeatureFlags.BFA_CALL_UPDATE_BILLING_STATUS)?.enabled ?? false;
  const navigate = useNavigate();
  const { show } = useSnackbar();
  const { t } = useTranslation('', { keyPrefix: 'fullScreenBanner' });
  const billingAccount = useBillingAccount().data;
  const billingWallet = useFetchBilling().data;

  const staticShow = useRef(show);

  useEffect(() => {
    const handleGoToBilling = () => {
      navigate('/account/billing');
    };

    const isGracePeriodExpired = billingAccount?.grace_period_end_timestamp
      ? new Date(billingAccount.grace_period_end_timestamp) < new Date()
      : false;

    if (
      alertFlag &&
      billingWallet &&
      !isGracePeriodExpired &&
      billingAccount?.grace_period_end_timestamp
    ) {
      const remainingHours = differenceInHours(
        new Date(billingAccount.grace_period_end_timestamp),
        new Date(),
      );
      const message = t('creditCardExpired.gracePeriodHasBeenActivated', { count: remainingHours });

      staticShow.current({
        action: {
          label: t('pastDueInvoices.actionLabel'),
          onClick: handleGoToBilling,
        },
        autoHideDuration: null,
        message,
        variant: 'warning',
      });
    } else if (
      alertFlag &&
      billingWallet &&
      billingAccount?.should_show_credit_expiration_message
    ) {
      const expirationDate = getExpirationDate(
        billingWallet.stripe_cc_exp_month,
        billingWallet.stripe_cc_exp_year,
      );
      const message = t('creditCardExpired.approachingCreditCardExpiration', {
        cardLastFour: billingWallet.stripe_cc_last4,
        cardType: billingWallet.stripe_cc_brand,
        expirationDate,
      });

      staticShow.current({
        action: {
          label: t('pastDueInvoices.actionLabel'),
          onClick: handleGoToBilling,
        },
        autoHideDuration: null,
        message,
        variant: 'warning',
      });
      billingAccountService.updateShouldShowCreditExpirationMessage();
    }
  }, [alertFlag, billingAccount, staticShow, t, billingWallet]);
};
